"use client";

import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { ButtonHTMLAttributes, forwardRef } from "react";
import { cn } from "@/lib/utils";

interface CTAButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "ghost";
  size?: "default" | "lg" | "sm";
  href?: string;
  showArrow?: boolean;
  loading?: boolean;
}

const CTAButton = forwardRef<HTMLButtonElement, CTAButtonProps>(
  (
    {
      children,
      variant = "primary",
      size = "default",
      href,
      showArrow = false,
      loading = false,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    const baseStyles = cn(
      "relative inline-flex items-center justify-center font-body font-medium",
      "transition-all duration-200 ease-out",
      "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/50",
      "disabled:opacity-50 disabled:cursor-not-allowed"
    );

    const variants = {
      primary: cn(
        "bg-primary text-primary-foreground",
        "hover:bg-primary/90 active:scale-[0.98]",
        "shadow-md hover:shadow-lg"
      ),
      secondary: cn(
        "bg-secondary text-secondary-foreground border border-border",
        "hover:bg-secondary/80 active:scale-[0.98]"
      ),
      ghost: cn("text-foreground hover:bg-secondary/60"),
    };

    const sizes = {
      default: "px-8 py-4 text-base rounded-2xl",
      lg: "px-10 py-5 text-lg rounded-2xl",
      sm: "px-6 py-3 text-sm rounded-xl",
    };

    const buttonContent = (
      <>
        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {children}
        {showArrow && !loading && (
          <motion.span
            className="ml-2"
            initial={{ x: 0 }}
            whileHover={{ x: 4 }}
            transition={{ duration: 0.2 }}
          >
            <ArrowRight className="h-4 w-4" />
          </motion.span>
        )}
      </>
    );

    const combinedClassName = cn(
      baseStyles,
      variants[variant],
      sizes[size],
      className
    );

    if (href && !disabled) {
      return (
        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <Link to={href} className={combinedClassName}>
            {buttonContent}
          </Link>
        </motion.div>
      );
    }

    return (
      <motion.button
        ref={ref}
        whileHover={{ scale: disabled ? 1 : 1.02 }}
        whileTap={{ scale: disabled ? 1 : 0.98 }}
        className={combinedClassName}
        disabled={disabled || loading}
        {...(props as any)}
      >
        {buttonContent}
      </motion.button>
    );
  }
);

CTAButton.displayName = "CTAButton";

export { CTAButton };
